import * as moment from 'moment';
import { NOTES_COPY } from '../constants';
import {
  ContactsUserData,
  InsuranceUserData,
  OrderWizardStatus,
  ProductsUserData,
  ProviderUserData,
} from '../store/order-wizard/order-wizard-data/order-wizard-data.model';
import { DeductibleOverridesFormValues, PatientCopay } from './copay.model';
import { DatesOfService } from './dates-of-service.model';
import {
  ApiPatientInsurancePolicy,
  buildNewInsurancePolicyRequestData,
  InsuranceType,
  PatientInsurance,
} from './insurance.model';
import { Order } from './order.model';
import { Provider, ProviderVisitFormValues, ProviderVisitVerification } from './provider.model';
import { DaysOnHandRequired } from './reorder-product-requirements.model';
import { ChargeTypeLong, ReorderDetails } from './reorder.model';
import { omit } from 'lodash-es';
import { join } from '../utils/common';
import { FormatCurrencyPipe } from '../utils/pipes';
import { AddressType, PatientAddress } from './address.model';
import { YesOrNo } from './common.model';

export enum DoctorVisitType {
  NotRequired = 0,
  Verified = 1,
  New = 2,
}

export enum ChargeTypeShort {
  Insurance = 'Y',
  NoCharge = 'N',
  CashSale = 'C',
}

export interface SubmitReorderNewDoctor {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  npi: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

export interface SubmitReorderRequestItem {
  itemId: number;
  quantity: number;
  hasValidPwo: boolean;
  claimDate: string;
  chargeType?: ChargeTypeShort;
  daysOnHand?: number | null;
  isDuplicate?: boolean;
  originalQuantity?: number;
}

export interface ApiSubmitReorderRequest {
  patientId: number;
  portalSessionId: number;
  shipmentTypeId: number;
  targetedShipDate: string;
  spokeTo: 'patient';
  policies: ApiPatientInsurancePolicy[];
  items: SubmitReorderRequestItem[];
  reorderCardId?: number;
  shipmentId?: number;
  activityId?: 5;
  isManualShipment?: YesOrNo.N;
  shippingMethod?: string;
  lastDoctorVisitType?: DoctorVisitType;
  lastDoctorVisitDate?: string;
  glucoseTestingFrequency?: number | null;
  cgmSiteChangeFrequency?: number | null;
  pumpSiteChangeFrequency?: number | null;
  outcomeId?: null;
  subOutcomeId?: null;
  reorderCardSignatureDate?: null;
  verifiedDoctorId?: number;
  pecosRegisteredDoctor?: boolean | null;
  pecosSanctionedDoctor?: boolean | null;
  newDoctor?: SubmitReorderNewDoctor;
  verifiedPrimaryInsurance?: string;
  verifiedSecondaryInsurance?: string;
  ouDaysOnHand?: number | null;
  userNote?: string;
  shippingAddressId?: number;
  submitToAgent?: boolean;
}

export const getShortChargeType = (chargeType: ChargeTypeLong): ChargeTypeShort => {
  switch (chargeType) {
    case ChargeTypeLong.Insurance:
      return ChargeTypeShort.Insurance;
    case ChargeTypeLong.NoCharge:
      return ChargeTypeShort.NoCharge;
    case ChargeTypeLong.CashSale:
      return ChargeTypeShort.CashSale;
  }
};

export const getProviderValues = (
  providerUserData: ProviderUserData,
  providers: Provider[]
): {
  newDoctor?: SubmitReorderNewDoctor;
  verifiedDoctorId: number;
  pecosRegisteredDoctor?: boolean | null;
  pecosSanctionedDoctor?: boolean | null;
} => {
  const newProviderData = providerUserData.newProviderFormValues;
  if (!newProviderData) {
    const provider = providers.find((p) => p.id === providerUserData.selectedProviderId)!;
    return {
      verifiedDoctorId: provider.id,
      pecosRegisteredDoctor: provider.pecosRegistered,
      pecosSanctionedDoctor: provider.sanctioned,
    };
  } else {
    return {
      newDoctor: {
        firstName: newProviderData.providerFirstName,
        lastName: newProviderData.providerLastName,
        phone: newProviderData.phoneNumber,
        npi: newProviderData.npi,
        addressLine1: newProviderData.addressLine1,
        addressLine2: newProviderData.addressLine2,
        city: newProviderData.addressCity,
        state: newProviderData.addressState,
        zipCode: newProviderData.addressZip,
      },
      verifiedDoctorId: 0,
      pecosRegisteredDoctor: null,
      pecosSanctionedDoctor: null,
    };
  }
};

export const getInsuranceValues = (
  insuranceUserData: InsuranceUserData,
  insurances: PatientInsurance
): {
  verifiedPrimaryInsurance: string;
  verifiedSecondaryInsurance: string;
  policies: ApiPatientInsurancePolicy[];
} => {
  let verifiedPrimaryInsurance = '';
  let verifiedSecondaryInsurance = '';
  let policies = [];

  if (!!insuranceUserData.primaryInsurance) {
    policies.push(buildNewInsurancePolicyRequestData(insuranceUserData.primaryInsurance, InsuranceType.Primary));
  } else if (insurances.primaryInsurance?.insurancePlan) {
    verifiedPrimaryInsurance = insurances.primaryInsurance.insurancePlan;
  }

  if (!!insuranceUserData.secondaryInsurance) {
    policies.push(buildNewInsurancePolicyRequestData(insuranceUserData.secondaryInsurance, InsuranceType.Secondary));
  } else if (insurances.secondaryInsurance?.insurancePlan) {
    verifiedSecondaryInsurance = insurances.secondaryInsurance.insurancePlan;
  }

  return { verifiedPrimaryInsurance, verifiedSecondaryInsurance, policies };
};

export const getDoctorVisitValues = (
  isDoctorVisitVerificationRequired: boolean,
  doctorVisits: ProviderVisitFormValues
): {
  lastDoctorVisitType: DoctorVisitType;
  lastDoctorVisitDate?: string;
} => {
  let lastDoctorVisitType = DoctorVisitType.NotRequired;
  let lastDoctorVisitDate = undefined;

  if (isDoctorVisitVerificationRequired) {
    if (doctorVisits.nextProviderVisit) {
      lastDoctorVisitType = DoctorVisitType.New;
      lastDoctorVisitDate = moment(doctorVisits.nextProviderVisit).format('YYYY-MM-DD');
    } else {
      lastDoctorVisitType = DoctorVisitType.Verified;
      lastDoctorVisitDate = moment(doctorVisits.lastProviderVisit).format('YYYY-MM-DD');
    }
  }

  return { lastDoctorVisitType, lastDoctorVisitDate };
};

const formatCurrencyPipe = new FormatCurrencyPipe();

export const createUserNote = (
  canAutoComputeTargetShipDate: OrderWizardStatus,
  doNotFetchCopay: OrderWizardStatus,
  hideSummaryStepBillingInfo: OrderWizardStatus,
  skipBillingStep: OrderWizardStatus,
  submitToAgent: OrderWizardStatus,
  patientCopay: PatientCopay | null,
  appliedDeductibleOverrides: DeductibleOverridesFormValues | null,
  userWillPayLater: boolean
): string => {
  const notes = [];

  if (submitToAgent.value) {
    notes.push('Submitting Order to Agent');
    for (let k in omit(submitToAgent.debugInfo, 'hideSummaryStepBillingInfo')) {
      if (submitToAgent.debugInfo[k]) notes.push((NOTES_COPY as any)[k]);
    }
  }

  const estimatedResponsibility = patientCopay?.estimatedPatientResponsibility;
  const balance = patientCopay?.outstandingBalance;
  const estimatedCopayment = patientCopay?.estimatedInsuranceCopayment;
  const excludeCopay = patientCopay?.excludeCopay;

  const formatCurrency = (n?: number) => formatCurrencyPipe.transform(n);
  const pdo = appliedDeductibleOverrides?.overridePrimaryDeductible;
  const sdo = appliedDeductibleOverrides?.overrideSecondaryDeductible;

  const copayDetailsString = join(
    [
      `copay excluded: ${excludeCopay ? 'yes' : 'no'}`,
      pdo ? 'user marked primary deductible as met' : null,
      sdo ? 'user marked secondary deductible as met' : null,
    ],
    ',',
    '',
    true
  );

  const copayString = `\
${formatCurrency(estimatedResponsibility) || 'unknown'}: ${formatCurrency(balance) || 'unknown'} \
balance and ${formatCurrency(estimatedCopayment) || 'unknown'} estimated copay (\
${copayDetailsString}\
)\
`;

  if (hideSummaryStepBillingInfo.value) {
    notes.push('Could not collect payment at this time');
    for (let k in hideSummaryStepBillingInfo.debugInfo) {
      if (hideSummaryStepBillingInfo.debugInfo[k]) notes.push((NOTES_COPY as any)[k]);
    }
    for (let k in doNotFetchCopay.debugInfo) {
      if (doNotFetchCopay.debugInfo[k]) notes.push((NOTES_COPY as any)[k]);
    }
    for (let k in canAutoComputeTargetShipDate.debugInfo) {
      if (canAutoComputeTargetShipDate.debugInfo[k]) notes.push((NOTES_COPY as any)[k]);
    }
  } else if (skipBillingStep.value) {
    notes.push('No collection needed');
    notes.push(`Patient responsibility is ${copayString}`);

    if (patientCopay?.hasPaymentPlan) notes.push('Patient has payment plan');

    if (patientCopay?.hasRentalSchedule) notes.push('Patient has rental schedule');

    for (let k in omit(skipBillingStep.debugInfo, 'hideSummaryStepBillingInfo')) {
      if (skipBillingStep.debugInfo[k]) notes.push((NOTES_COPY as any)[k]);
    }
  } else if (userWillPayLater) {
    notes.push('Patient requested to pay later');
    notes.push(`If patient calls in, collect patient responsibility of ${copayString}`);
  } else if (submitToAgent.value && submitToAgent.debugInfo.hasPaymentFailed) {
    notes.push(`Failed to collect patient responsibility of ${copayString}`);
  } else {
    notes.push(`Collected patient responsibility of ${copayString}`);
  }

  return join(notes.filter((n) => !!n));
};

export const buildSubmitReorderRequest = (
  patientId: number,
  reorder: ReorderDetails,
  computedTargetShipDate: string,
  matchingOrder: Order,
  datesOfService: DatesOfService | null,
  providerVisitVerification: ProviderVisitVerification | null,
  providerUserData: ProviderUserData,
  providers: Provider[],
  productUserData: ProductsUserData,
  insurances: PatientInsurance,
  insuranceUserData: InsuranceUserData,
  daysOnHandRequired: DaysOnHandRequired[],
  submitToAgent: OrderWizardStatus,
  contactsUserData: ContactsUserData,
  addresses: PatientAddress[],
  isOuShipment: boolean,
  userNote: string,
  sessionId?: number
): ApiSubmitReorderRequest => ({
  patientId,
  portalSessionId: sessionId || 1, // TODO Remove temp fake sessionId when test patients are okta users
  reorderCardId: matchingOrder.isShipment ? undefined : reorder.id,
  shipmentId: matchingOrder.isShipment ? reorder.id : undefined,
  shipmentTypeId: reorder.shipmentTypeId!,
  targetedShipDate: computedTargetShipDate,
  spokeTo: 'patient',
  items: reorder.items.map((i) => {
    const quantityToSubmit = productUserData.quantities?.find(
      (quantityItem) => quantityItem.itemUiId === i.uiId
    )?.quantityToSubmit;
    const itemQuantity = !!quantityToSubmit || quantityToSubmit === 0 ? quantityToSubmit : i.quantity;

    const dohToSubmit = productUserData.dohValues?.find((dohItem) => dohItem.itemId === i.id)?.doh;
    const dohRequired = daysOnHandRequired.find((dohr) => dohr.itemId === i.id);
    const itemDoh =
      dohRequired && dohRequired.daysOnHandRequired && !dohToSubmit ? dohRequired.estimatedDaysOnHand : dohToSubmit;

    return {
      itemId: i.id,
      hasValidPwo: !i.isMissingDoc,
      chargeType: getShortChargeType(i.chargeType),
      isDuplicate: i.isDuplicate,
      quantity: itemQuantity!,
      originalQuantity: i.quantity,
      claimDate:
        datesOfService?.items.find((dosItem) => dosItem.uiId === i.uiId)?.calculatedDos || computedTargetShipDate,
      daysOnHand: itemDoh,
    };
  }),
  activityId: 5,
  isManualShipment: YesOrNo.N,
  shippingMethod: matchingOrder.mailClass || 'A',
  glucoseTestingFrequency: productUserData.frequencies?.patientTestFreq,
  cgmSiteChangeFrequency: productUserData.frequencies?.cgmSiteChgFreq,
  pumpSiteChangeFrequency: productUserData.frequencies?.pumpSiteChgFreq,
  ouDaysOnHand: isOuShipment
    ? productUserData.ouDohValue !== null
      ? productUserData.ouDohValue
      : computedTargetShipDate
      ? Math.max(moment(computedTargetShipDate).startOf('day').diff(moment().startOf('day'), 'days'), 0)
      : 0
    : undefined,
  outcomeId: null,
  subOutcomeId: null,
  reorderCardSignatureDate: null,
  userNote,
  ...getDoctorVisitValues(providerVisitVerification?.required || false, providerUserData.providerVisitFormValues),
  ...getProviderValues(providerUserData, providers),
  ...getInsuranceValues(insuranceUserData, insurances),
  shippingAddressId:
    contactsUserData.selectedAddressId ||
    reorder.shippingAddress?.id ||
    matchingOrder.shippingAddress?.id ||
    addresses.find((a) => a.types.includes(AddressType.Shipping))?.id,
  submitToAgent: submitToAgent.value,
});
